/**
 * D2.2 — physical damage model.
 *
 * DamageEvent = SIGNAL, material physics = TRANSFER FUNCTION, one resolver.
 * Named tools (cannon / bomb) are parameter presets over the continuous space.
 * Fragmentation count/size EMERGES from surplus energy ÷ fracture energy
 * (Grady–Kipp style), not authored minPieces/maxPieces.
 */

import type { Rng, Vec3 } from './fracture/voronoiFracture';
import {
  CHARGE_DENSITY,
  CHARGE_RADIUS,
  CHARGE_SPEED,
  CANNONBALL_DENSITY,
  CANNONBALL_RADIUS,
  CANNONBALL_SPEED,
  CONCUSSION_DENSITY,
  CONCUSSION_RADIUS,
  CONCUSSION_SPEED,
  RUBBLE_DENSITY,
  RUBBLE_RADIUS,
  RUBBLE_SPEED,
  SPIKE_DENSITY,
  SPIKE_RADIUS,
  SPIKE_SPEED,
} from './projectileConstants';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Five compact demolition actions, all routed through the same material model. */
export type DemolitionWeapon =
  | 'cannon'
  | 'bomb'
  | 'spike'
  | 'rubble'
  | 'concussion';

/** Shaped energy kernel for focused point footprints (kinetic weapons). */
export interface DirectionalKernel {
  /** Energy reach along the impact axis (m). */
  depth: number;
  /** Energy radius perpendicular to the axis (m). */
  lateral: number;
  /** Sharpness of the lateral cutoff; higher = narrower channel. */
  lateralExponent: number;
}

export interface DamageEvent {
  origin: Vec3;
  footprint:
    | { kind: 'point'; effectiveRadius: number; shape?: DirectionalKernel }
    | { kind: 'sphere'; radius: number; falloff: 'inverseSquare' | 'linear' };
  temporal: { kind: 'impulse' };
  direction: { kind: 'focused'; vector: Vec3 } | { kind: 'omni' };
  modeMix: { compressive: number; tensile: number; shear: number };
  energy: number; // Joules
}

export interface MaterialStrength {
  compressive: number;
  tensile: number;
  shear: number;
}

export interface MaterialAnisotropy {
  /** Unit axis of grain / fiber (world/local; wood uses +X). */
  axis: [number, number, number];
  /** Multiplier on strength when loading is fully across-grain (0..1). */
  crossGrainFactor: number;
}

/** Physical transfer-function block on DestructionProfile. */
export interface MaterialPhysics {
  strength: MaterialStrength;
  anisotropy?: MaterialAnisotropy;
  /** J/m² to create new crack surface. */
  fractureEnergy: number;
  /** 0..1 fraction of surplus energy → fragmentation (vs heat/deformation). */
  brittleness: number;
  /** 0..1 rim weakening / damage accumulation scale. */
  damageAccumRate: number;
}

export interface CellEnergyDeposit {
  cellId: number;
  /** Delivered energy to this cell (J). */
  energy: number;
  dist: number;
  volume: number;
  /** Stress proxy σ = e / V. */
  sigma: number;
  /** Failure threshold energy for this cell (J). */
  thresholdE: number;
  /** Effective strength used (mode-weighted, anisotropy-adjusted). */
  effectiveStrength: number;
  failed: boolean;
}

export interface SubFracturePlan {
  cellId: number;
  /** Extra pieces to request beyond the parent (total pieces ≈ 1 + extra). */
  extraPieces: number;
}

export interface ImpulsePlan {
  cellId: number;
  /** World-space launch velocity (pre-clamp). */
  vx: number;
  vy: number;
  vz: number;
  speed: number;
  clamped: boolean;
}

/**
 * Finite launch velocity recorded onto an impact-trace release.
 * Missing / non-finite plans become zeros — never NaN or undefined.
 */
export function launchVelocityFromImpulse(
  plan: Pick<ImpulsePlan, 'vx' | 'vy' | 'vz' | 'speed'> | null | undefined,
): { vx: number; vy: number; vz: number; speed: number } {
  if (!plan) return { vx: 0, vy: 0, vz: 0, speed: 0 };
  const vx = Number.isFinite(plan.vx) ? plan.vx : 0;
  const vy = Number.isFinite(plan.vy) ? plan.vy : 0;
  const vz = Number.isFinite(plan.vz) ? plan.vz : 0;
  const speed = Number.isFinite(plan.speed) ? plan.speed : Math.hypot(vx, vy, vz);
  return { vx, vy, vz, speed };
}

export interface RimDamageUpdate {
  cellId: number;
  /** Damage to ADD (not absolute). */
  delta: number;
  /** Accumulated after add (caller may not have prior). */
  sigmaRatio: number;
}

export interface ResolveResult {
  deposits: CellEnergyDeposit[];
  carvedCellIds: number[];
  /** Sum of failed cell volumes (m³). */
  carvedVolume: number;
  /**
   * carvedVolume / totalCandidateVolume. Bridge exposes this as
   * lastResolve.carvedVolumeFrac (wall fraction on first fracture).
   */
  carvedVolumeFrac: number;
  surplusEnergy: number;
  newSurfaceArea: number;
  spentSurfaceArea: number;
  subPlans: SubFracturePlan[];
  impulses: ImpulsePlan[];
  rimUpdates: RimDamageUpdate[];
  /** Cells whose accumulated damage reaches ≥1 this resolve (pre-subdivide). */
  preCrackCellIds: number[];
  stats: {
    energy: number;
    carvedCells: number;
    carvedVolume: number;
    carvedVolumeFrac: number;
    surplusEnergy: number;
    newSurfaceArea: number;
    spentSurfaceArea: number;
    subPieces: number;
    clampedChunks: number;
    /** Carved chunks raised to the applicable minimum launch speed. */
    flooredChunks: number;
    maxChunkSpeed: number;
  };
}

// ---------------------------------------------------------------------------
// Calibration constants (Task 4 — tune for e2e parity bands)
// ---------------------------------------------------------------------------

/**
 * Global strength scale: cell fails when delivered energy exceeds
 * effectiveStrength × K × V^α (see THRESHOLD_VOLUME_EXPONENT).
 * Absorbs unit conversion from MPa-like strengths into energy units.
 * Calibrated so concrete center cannonball ≈ 15–40 cells / 2–10% volume.
 */
export let DAMAGE_STRENGTH_K = 28;

/**
 * Volume exponent on the failure threshold (D2.2b):
 *   e_thresh = se · K · V_ref · (V / V_ref)^α
 * α = 1 → pure energy-density (σ = e/V); favors failing small cells.
 * α = 2/3 → area-scaled capacity (cracks care about cross-section).
 * Anchored at THRESHOLD_VOLUME_REF so median cells keep α=1 thresholds while
 * large rim slabs become relatively weaker (and fines relatively tougher).
 *
 * Calibrated α=0.95: mild area blend that preserves cannon 15–40 / 2–10% vol
 * and concrete bomb 18–35% vol at BOMB_ENERGY_J. Stronger α (<0.9) drops
 * cannon below the secondary count band without unlocking large-slab fails
 * inside the volume gate (large cells sit outside the blast core; reaching
 * them cascades past 35% wall volume via crack propagation).
 */
export let THRESHOLD_VOLUME_EXPONENT = 0.95;

/**
 * Characteristic cell volume (m³) for threshold anchoring. ≈ concrete median.
 * Not a free material tune — fixed dimensional anchor for the exponent.
 */
export const THRESHOLD_VOLUME_REF = 0.1;

/**
 * Default bomb chemical energy (J).
 *
 * Model units: K absorbs MPa→energy conversion, so this is NOT free-field TNT
 * joules. Offline concrete partition sweep (K=28, α=0.95): 6 kJ → ~34% wall
 * volume carved (mid–top of 18–35% gate); ≥7 kJ jumps past 40% via cascade;
 * 50–200 kJ annihilates the wall and stone. 6 kJ is already ~3× cannon KE
 * (~1.9 kJ @ 28 m/s) and carves ~5× the volume — breaching-scale relative to
 * the kinetic tool inside this unit system.
 */
export let BOMB_ENERGY_J = 6_000;

/** Smaller omni blast carried by the concussion canister. */
export const CONCUSSION_ENERGY_J = 3_200;

/** Cannonball contact footprint (m). */
/** Contact-scale footprint; ~old damage-radius band so center shot carves 15–40. */
export const CANNON_EFFECTIVE_RADIUS = 1.15;

/**
 * The cannon deliberately has NO directional kernel: its isotropic
 * (1 − d/r)² ball at CANNON_EFFECTIVE_RADIUS over a 0.6 m wall already reads
 * as "wide crater" — that falloff IS the shipped calibration anchor
 * (15–40 cells / 2–10% vol at the 28 m/s centre probe). Attempts to express
 * the same behaviour as a depth×lateral kernel redistribute normalized energy
 * away from mid-lateral cells (measured: 17 → 13 carved) and turn a working
 * calibration into a curve-fitting exercise. Shaped kernels are reserved for
 * the weapons whose identity the isotropic ball CANNOT express: the spike's
 * channel and the rubble bruise. Revisit if walls ever get thick enough that
 * a cannon bowl differs observably from the ball.
 */

/** Spike: narrow deep channel that reaches through a 0.6 m wall. */
export const SPIKE_KERNEL: DirectionalKernel = {
  depth: 1.1,
  lateral: 0.38,
  lateralExponent: 2.2,
};

/** Rubble: broad surface bruise — short depth, wide lateral, soft cutoff. */
export const RUBBLE_KERNEL: DirectionalKernel = {
  depth: 0.28,
  lateral: 1.35,
  lateralExponent: 1.0,
};

/**
 * A fully cracked rim retains some interlock and aggregate bridging, but it is
 * no longer a pristine cell. This turns the existing rim-damage record into
 * real fatigue: repeated impacts at the same edge need less energy to release
 * the next piece instead of resolving as identical independent events.
 */
export const FULL_RIM_DAMAGE_STRENGTH_LOSS = 0.65;

export function damageStrengthMultiplier(damage: number): number {
  const normalized = Math.max(0, Math.min(1, Number.isFinite(damage) ? damage : 0));
  return 1 - FULL_RIM_DAMAGE_STRENGTH_LOSS * normalized;
}

/** Bomb spherical footprint radius (m). */
export const BOMB_SPHERE_RADIUS = 2.6;

/**
 * Hopkinson–Cranz cube-root scaling: radius at a given overpressure scales as
 * E^(1/3). At power === 1 this is exactly 1 so default-power carve radii stay
 * bit-identical to the fixed BOMB_SPHERE_RADIUS.
 */
export function blastRadiusScale(power: number): number {
  if (!Number.isFinite(power) || power <= 0) return 1;
  return Math.cbrt(power);
}

/** Concussion canister footprint: broad impulse, less carve than a charge. */
export const CONCUSSION_SPHERE_RADIUS = 1.55;

/**
 * Cap on event energy that contributes to kinetic launch share.
 * Prevents absurd √(2E/m) from multi-charge stacking; MAX_LAUNCH_SPEED is the hard net.
 */
export const IMPULSE_ENERGY_CAP_J = 25_000;

/**
 * Omni (bomb) kinetic multiplier on energy fed into the launch pool.
 * Carve energy stays at BOMB_ENERGY_J (volume gates); this scales only the
 * kinetic share so fragments clear the wall. Paired with BLAST_MAX_LAUNCH so
 * volleys cannot stack past the e2e maxY gate (qa11 mul≥3 uncapped → 16 m).
 */
export const BLAST_KINETIC_MUL = 4;

/**
 * Per-chunk launch cap (m/s) for blast-carved fragments only (spawn + resolve).
 * Outer step-clamp MAX_LAUNCH_SPEED / RESOLVER_MAX_LAUNCH_SPEED (18) stays the
 * global safety net for post-contact / cannon paths.
 */
export const BLAST_MAX_LAUNCH = 8;

/**
 * Minimum ejection speed (m/s) for blast-carved chunks. Impulse-once cartoon
 * loses sustained gas expansion; this floor approximates residual push so
 * wedged fragments leave the 0.6 m wall instead of re-jamming at ~1 m/s.
 * Scaled by ResolveOptions.launchScale (charge power) so a weak charge
 * crumbles out and a strong one throws — identity at launchScale === 1.
 */
export const BLAST_MIN_LAUNCH = 2.5;

/**
 * Ejecta split between the driving direction and the free face it escapes
 * through. Shared by both paths: blasts blend radial-from-charge with the nearer
 * wall face, focused impacts blend travel with the face the projectile came from.
 * Without the out-of-face share, kinetic ejecta was driven INTO the 0.6 m cavity
 * and wedged there, turning the crater into a cushion that absorbed later shots.
 */
export const KINETIC_THROUGH = 0.45;
export const KINETIC_OUT_OF_FACE = 0.55;
/** Minimum focused-carve launch speed (m/s) so crater ejecta clear the cavity. */
export const KINETIC_MIN_LAUNCH = 1.2;

/** Surface-area coefficient: each new piece of volume V adds ≈ k · V^(2/3) m². */
export const SURFACE_AREA_K = 0.55;

/** Max extra pieces per carved parent from energy budget (before profile budget). */
export const MAX_EXTRA_PIECES_PER_CELL = 9;

/**
 * Crack-propagation: fraction of a failed cell's surplus transferred to neighbors
 * so brittle materials (glass) can cascade to full release without a special case.
 * Scaled by brittleness² in the resolver so concrete stays local while glass runs.
 */
export const CRACK_PROPAGATION_ALPHA = 0.55;
export const CRACK_PROPAGATION_MAX_ITERS = 24;

/** Hard launch clamp (m/s) — shared with demolitionPhysics. */
export const RESOLVER_MAX_LAUNCH_SPEED = 18;

// Test-only mutators (realism calibration / unit tests).
export function setDamageStrengthK(k: number) {
  DAMAGE_STRENGTH_K = k;
}
export function setBombEnergyJ(j: number) {
  BOMB_ENERGY_J = j;
}
export function setThresholdVolumeExponent(alpha: number) {
  THRESHOLD_VOLUME_EXPONENT = alpha;
}

// ---------------------------------------------------------------------------
// Cannonball mass / KE
// ---------------------------------------------------------------------------

const BALL_VOLUME = (4 / 3) * Math.PI * CANNONBALL_RADIUS ** 3;

/** Cannonball mass (kg) from density × sphere volume. */
export const CANNONBALL_MASS = CANNONBALL_DENSITY * BALL_VOLUME;

/** Nominal muzzle KE at CANNONBALL_SPEED (J). */
export const CANNONBALL_MUZZLE_ENERGY =
  0.5 * CANNONBALL_MASS * CANNONBALL_SPEED * CANNONBALL_SPEED;

export function cannonballKineticEnergy(speed: number): number {
  return 0.5 * CANNONBALL_MASS * speed * speed;
}

function sphereMass(radius: number, density: number): number {
  return density * ((4 / 3) * Math.PI * radius ** 3);
}

export const SPIKE_MASS = sphereMass(SPIKE_RADIUS, SPIKE_DENSITY);
export const RUBBLE_MASS = sphereMass(RUBBLE_RADIUS, RUBBLE_DENSITY);
export const CHARGE_MASS = sphereMass(CHARGE_RADIUS, CHARGE_DENSITY);
export const CONCUSSION_MASS = sphereMass(CONCUSSION_RADIUS, CONCUSSION_DENSITY);

export const SPIKE_MUZZLE_ENERGY = 0.5 * SPIKE_MASS * SPIKE_SPEED * SPIKE_SPEED;
export const RUBBLE_MUZZLE_ENERGY = 0.5 * RUBBLE_MASS * RUBBLE_SPEED * RUBBLE_SPEED;
export const CHARGE_MUZZLE_ENERGY = 0.5 * CHARGE_MASS * CHARGE_SPEED * CHARGE_SPEED;
export const CONCUSSION_MUZZLE_ENERGY =
  0.5 * CONCUSSION_MASS * CONCUSSION_SPEED * CONCUSSION_SPEED;

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

/**
 * Fracture-energy transfer for live cannon contacts.
 * Live approach is ~muzzle (55 m/s) but material calibration was locked at the
 * older 28 m/s KE band; scale KE by (28/55)² so strengths stay put.
 */
export const CANNON_FRACTURE_ENERGY_TRANSFER = (28 / 55) ** 2;

/**
 * Kinetic fracture event from a focused projectile.
 * `energyTransfer` multiplies ½mv² into material energy (default 1 for probes /
 * synthetic callers; live processContacts passes an explicit per-weapon scale).
 */
export function cannonballEvent(
  hitPoint: Vec3,
  velocityVector: Vec3,
  energyTransfer: number = 1,
): DamageEvent {
  const speed = Math.hypot(velocityVector.x, velocityVector.y, velocityVector.z) || 1e-6;
  const energy = cannonballKineticEnergy(speed) * energyTransfer;
  return {
    origin: { ...hitPoint },
    footprint: {
      kind: 'point',
      effectiveRadius: CANNON_EFFECTIVE_RADIUS,
      // No shape: the isotropic ball is the cannon's calibrated crater —
      // see the note above the kernel constants.
    },
    temporal: { kind: 'impulse' },
    direction: {
      kind: 'focused',
      vector: {
        x: velocityVector.x / speed,
        y: velocityVector.y / speed,
        z: velocityVector.z / speed,
      },
    },
    modeMix: { compressive: 0.75, tensile: 0.05, shear: 0.2 },
    energy,
  };
}

function focusedKineticEvent(
  hitPoint: Vec3,
  velocityVector: Vec3,
  mass: number,
  effectiveRadius: number,
  modeMix: DamageEvent['modeMix'],
  energyTransfer: number = 1,
  shape?: DirectionalKernel,
): DamageEvent {
  const speed = Math.hypot(velocityVector.x, velocityVector.y, velocityVector.z) || 1e-6;
  return {
    origin: { ...hitPoint },
    footprint: { kind: 'point', effectiveRadius, shape },
    temporal: { kind: 'impulse' },
    direction: {
      kind: 'focused',
      vector: {
        x: velocityVector.x / speed,
        y: velocityVector.y / speed,
        z: velocityVector.z / speed,
      },
    },
    modeMix,
    energy: 0.5 * mass * speed * speed * energyTransfer,
  };
}

/** A narrow steel spike: smaller contact, more shear, focused into the wall. */
export function spikeEvent(
  hitPoint: Vec3,
  velocityVector: Vec3,
  energyTransfer: number = 1,
): DamageEvent {
  return focusedKineticEvent(hitPoint, velocityVector, SPIKE_MASS, 0.72, {
    compressive: 0.35,
    tensile: 0.1,
    shear: 0.55,
  }, energyTransfer, SPIKE_KERNEL);
}

/** A rough chunk of masonry: medium contact and a less directional shove. */
export function rubbleEvent(
  hitPoint: Vec3,
  velocityVector: Vec3,
  energyTransfer: number = 1,
): DamageEvent {
  return focusedKineticEvent(hitPoint, velocityVector, RUBBLE_MASS, 0.95, {
    compressive: 0.58,
    tensile: 0.12,
    shear: 0.3,
  }, energyTransfer, RUBBLE_KERNEL);
}

/** Smaller blast than a placed charge, with more tension/shear in the pulse. */
export function concussionEvent(
  center: Vec3,
  energy: number = CONCUSSION_ENERGY_J,
): DamageEvent {
  return {
    origin: { ...center },
    footprint: {
      kind: 'sphere',
      radius: CONCUSSION_SPHERE_RADIUS,
      falloff: 'inverseSquare',
    },
    temporal: { kind: 'impulse' },
    direction: { kind: 'omni' },
    modeMix: { compressive: 0.35, tensile: 0.35, shear: 0.3 },
    energy,
  };
}

export function weaponImpactEvent(
  weapon: DemolitionWeapon,
  hitPoint: Vec3,
  velocityVector: Vec3,
  energyTransfer: number = 1,
): DamageEvent {
  switch (weapon) {
    case 'spike':
      return spikeEvent(hitPoint, velocityVector, energyTransfer);
    case 'rubble':
      return rubbleEvent(hitPoint, velocityVector, energyTransfer);
    case 'concussion':
      return concussionEvent(hitPoint);
    case 'bomb':
      return bombEvent(hitPoint);
    case 'cannon':
    default:
      return cannonballEvent(hitPoint, velocityVector, energyTransfer);
  }
}

export function bombEvent(center: Vec3, energy: number = BOMB_ENERGY_J): DamageEvent {
  return {
    origin: { ...center },
    footprint: {
      kind: 'sphere',
      radius: BOMB_SPHERE_RADIUS,
      falloff: 'inverseSquare',
    },
    temporal: { kind: 'impulse' },
    direction: { kind: 'omni' },
    modeMix: { compressive: 0.55, tensile: 0.25, shear: 0.2 },
    energy,
  };
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function len3(v: Vec3): number {
  return Math.hypot(v.x, v.y, v.z);
}

function norm3(v: Vec3): Vec3 {
  const L = len3(v) || 1e-9;
  return { x: v.x / L, y: v.y / L, z: v.z / L };
}

function dot3(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

/** Falloff weight at distance (not yet normalized). */
export function falloffWeight(
  dist: number,
  footprint: DamageEvent['footprint'],
): number {
  if (footprint.kind === 'point') {
    if (dist > footprint.effectiveRadius) return 0;
    // Smooth contact kernel — denser near hit.
    const t = 1 - dist / Math.max(footprint.effectiveRadius, 1e-6);
    return t * t;
  }
  if (dist > footprint.radius) return 0;
  if (footprint.falloff === 'linear') {
    return 1 - dist / Math.max(footprint.radius, 1e-6);
  }
  // inverseSquare shell: 1 / (1 + (d/r0)²) with r0 = radius/3 for a soft core.
  const r0 = Math.max(footprint.radius / 3, 0.15);
  return 1 / (1 + (dist / r0) * (dist / r0));
}

/**
 * Directional kernel weight for a focused point footprint with shape.
 * axial < 0 (slightly in front of contact) is treated as face-level (axial=0).
 */
export function directionalKernelWeight(
  rel: Vec3,
  axis: Vec3,
  shape: DirectionalKernel,
): number {
  const axial = rel.x * axis.x + rel.y * axis.y + rel.z * axis.z;
  const axialForWeight = Math.max(axial, 0);
  const lx = rel.x - axial * axis.x;
  const ly = rel.y - axial * axis.y;
  const lz = rel.z - axial * axis.z;
  const lateral = Math.hypot(lx, ly, lz);
  const depth = Math.max(shape.depth, 1e-9);
  const latR = Math.max(shape.lateral, 1e-9);
  const wAx = Math.max(0, Math.min(1, 1 - axialForWeight / depth));
  const wLat = Math.pow(
    Math.max(0, Math.min(1, 1 - lateral / latR)),
    shape.lateralExponent,
  );
  return wAx * wLat;
}

/**
 * Apportion event.energy across candidate cells by footprint coverage.
 * Returns energy per cell id; Σ ≤ event.energy (normalized weights).
 *
 * For point footprints, an optional `preferredPrimaryCellId` that is among the
 * positive-weight candidates receives the 55% peak share. Distances and
 * event.origin are never relocated — only the peak assignment changes.
 *
 * When `footprint.shape` is set and the event is focused, weights come from the
 * directional kernel (axial × lateral). Shape-absent path is unchanged so
 * results stay bit-identical to the isotropic falloffWeight kernel.
 */
export function apportionEnergy(
  event: DamageEvent,
  candidates: Array<{ id: number; centroid: Vec3; volume: number }>,
  preferredPrimaryCellId?: number,
): Map<number, { energy: number; dist: number }> {
  const out = new Map<number, { energy: number; dist: number }>();
  let wSum = 0;
  const weights: Array<{ id: number; w: number; dist: number }> = [];
  const useShape =
    event.footprint.kind === 'point' &&
    event.footprint.shape != null &&
    event.direction.kind === 'focused';
  const shapeAxis =
    useShape && event.direction.kind === 'focused'
      ? norm3(event.direction.vector)
      : null;
  const shape =
    useShape && event.footprint.kind === 'point' ? event.footprint.shape! : null;

  for (const c of candidates) {
    const dist = Math.hypot(
      c.centroid.x - event.origin.x,
      c.centroid.y - event.origin.y,
      c.centroid.z - event.origin.z,
    );
    let w: number;
    if (shape && shapeAxis) {
      w = directionalKernelWeight(
        {
          x: c.centroid.x - event.origin.x,
          y: c.centroid.y - event.origin.y,
          z: c.centroid.z - event.origin.z,
        },
        shapeAxis,
        shape,
      );
    } else {
      w = falloffWeight(dist, event.footprint);
    }
    if (w <= 0) continue;
    weights.push({ id: c.id, w, dist });
    wSum += w;
  }
  if (wSum <= 0 || event.energy <= 0) return out;

  // Contact (point): put 55% of energy on the primary cell, distribute the rest
  // by footprint weight so large high-strength cells still see a peak load.
  if (event.footprint.kind === 'point' && weights.length > 0) {
    let primary =
      preferredPrimaryCellId != null
        ? weights.find((w) => w.id === preferredPrimaryCellId)
        : undefined;
    if (!primary) {
      // Deterministic nearest-centroid fallback (stable id on equal dist).
      weights.sort((a, b) => a.dist - b.dist || a.id - b.id);
      primary = weights[0];
    }
    const peakFrac = 0.55;
    const rem = event.energy * (1 - peakFrac);
    out.set(primary.id, {
      energy: event.energy * peakFrac + (rem * primary.w) / wSum,
      dist: primary.dist,
    });
    for (const { id, w, dist } of weights) {
      if (id === primary.id) continue;
      out.set(id, { energy: (rem * w) / wSum, dist });
    }
    return out;
  }

  for (const { id, w, dist } of weights) {
    out.set(id, { energy: (event.energy * w) / wSum, dist });
  }
  return out;
}

/**
 * Mode-weighted effective strength, with optional wood anisotropy.
 * `loadDir` is the loading direction (event vector or radial cell direction).
 */
export function effectiveStrength(
  physics: MaterialPhysics,
  modeMix: DamageEvent['modeMix'],
  loadDir: Vec3 | null,
): number {
  const s = physics.strength;
  let base =
    modeMix.compressive * s.compressive +
    modeMix.tensile * s.tensile +
    modeMix.shear * s.shear;
  if (physics.anisotropy && loadDir) {
    const axis = physics.anisotropy.axis;
    const a = norm3({ x: axis[0], y: axis[1], z: axis[2] });
    const d = norm3(loadDir);
    const align = Math.abs(dot3(a, d)); // 1 = along grain
    const factor =
      align + (1 - align) * physics.anisotropy.crossGrainFactor;
    base *= factor;
  }
  return Math.max(base, 1e-6);
}

/**
 * Threshold energy (J) for a cell of given volume.
 * e_thresh = se · K · V_ref · (V / V_ref)^α
 * with α = THRESHOLD_VOLUME_EXPONENT, V_ref = THRESHOLD_VOLUME_REF.
 * α=1 recovers pure energy-density e > se·K·V; α→2/3 area-scales capacity
 * so large slabs are not unnaturally immune relative to fines.
 */
export function thresholdEnergy(
  physics: MaterialPhysics,
  modeMix: DamageEvent['modeMix'],
  volume: number,
  loadDir: Vec3 | null,
  K: number = DAMAGE_STRENGTH_K,
  volumeExponent: number = THRESHOLD_VOLUME_EXPONENT,
): number {
  const se = effectiveStrength(physics, modeMix, loadDir);
  const V = Math.max(volume, 1e-9);
  const alpha = Number.isFinite(volumeExponent) ? volumeExponent : 1;
  const Vref = THRESHOLD_VOLUME_REF;
  // se·K·V_ref·(V/V_ref)^α  ==  se·K·V when α=1
  return se * K * Vref * Math.pow(V / Vref, alpha);
}

/** Surface area cost to split volume V into (1+extra) pieces (approx). */
export function surfaceAreaForExtraPieces(volume: number, extraPieces: number): number {
  if (extraPieces <= 0) return 0;
  const face = SURFACE_AREA_K * Math.pow(Math.max(volume, 1e-12), 2 / 3);
  return extraPieces * face;
}

/**
 * Greedily convert crack surface budget A into sub-fracture pieces on carved
 * cells nearest the origin. Returns plan + spent area.
 */
export function planSubFractureFromArea(
  carved: Array<{ cellId: number; volume: number; dist: number }>,
  surfaceBudget: number,
  maxSubBodies: number,
): { plans: SubFracturePlan[]; spent: number; totalPieces: number } {
  const sorted = carved.slice().sort((a, b) => a.dist - b.dist);
  const plans: SubFracturePlan[] = [];
  let spent = 0;
  let pieces = 0;
  for (const c of sorted) {
    if (pieces >= maxSubBodies) break;
    if (surfaceBudget - spent < 1e-12) break;
    let extra = 0;
    while (
      extra < MAX_EXTRA_PIECES_PER_CELL &&
      pieces + (extra + 1) <= maxSubBodies
    ) {
      const cost = surfaceAreaForExtraPieces(c.volume, 1);
      // cost is per piece; check remaining
      if (spent + cost > surfaceBudget + 1e-12) break;
      spent += cost;
      extra += 1;
      pieces += 1;
    }
    if (extra > 0) plans.push({ cellId: c.cellId, extraPieces: extra });
  }
  return { plans, spent, totalPieces: pieces };
}

export function loadDirectionForCell(
  event: DamageEvent,
  centroid: Vec3,
): Vec3 {
  if (event.direction.kind === 'focused') {
    return { ...event.direction.vector };
  }
  // Omni: radial from origin to cell (blast tension along radial).
  const d = {
    x: centroid.x - event.origin.x,
    y: centroid.y - event.origin.y,
    z: centroid.z - event.origin.z,
  };
  if (len3(d) < 1e-9) return { x: 0, y: 1, z: 0 };
  return norm3(d);
}

// ---------------------------------------------------------------------------
// Resolver (operates on cell descriptors — runtime-agnostic core)
// ---------------------------------------------------------------------------

export interface ResolveCandidate {
  id: number;
  centroid: Vec3;
  volume: number;
  /** Prior accumulated damage 0..1+. */
  damage: number;
  /** Mass for impulse (density × volume). */
  mass: number;
  /** Neighbor cell ids (for crack propagation). */
  neighbors: number[];
}

export interface ResolveOptions {
  physics: MaterialPhysics;
  candidates: ResolveCandidate[];
  event: DamageEvent;
  /** Sub-fracture body budget remaining this shot/volley. */
  subBudget: number;
  /** Upward bias for omni / radial impulses. */
  upwardBias: number;
  /** Deterministic rng for cone jitter. */
  rng: Rng;
  K?: number;
  maxLaunchSpeed?: number;
  /** Prior damage map (mutated via rimUpdates; preCrack when ≥1). */
  existingDamage?: Map<number, number>;
  /**
   * Point-footprint primary cell for the 55% energy peak. Must be a positive-
   * weight candidate; otherwise nearest-centroid fallback. Origin unchanged.
   */
  preferredPrimaryCellId?: number;
  /**
   * Multiplier on BLAST_MIN_LAUNCH for omni (bomb) floors only. Defaults to 1
   * so callers that omit it stay bit-identical to the pre-slider floor.
   * Threaded from clamped charge power — not smuggled through DamageEvent.
   */
  launchScale?: number;
}

/**
 * Core physical resolver. Pure w.r.t. world — no wasm, no body mutation.
 * Crack-propagation transfers surplus to neighbors so brittle panes can fully
 * release without a material-specific fullRelease flag.
 */
export function resolveDamageCore(opts: ResolveOptions): ResolveResult {
  const {
    physics,
    candidates,
    event,
    subBudget,
    upwardBias,
    rng,
    K = DAMAGE_STRENGTH_K,
    maxLaunchSpeed = RESOLVER_MAX_LAUNCH_SPEED,
  } = opts;
  // Non-finite / non-positive → identity (1). Cap keeps a miswired scale from
  // demanding a floor above BLAST_MAX_LAUNCH (still hard-capped below).
  const rawLaunchScale = opts.launchScale ?? 1;
  const launchScale =
    Number.isFinite(rawLaunchScale) && rawLaunchScale > 0
      ? Math.min(rawLaunchScale, BLAST_MAX_LAUNCH / BLAST_MIN_LAUNCH)
      : 1;

  const byId = new Map(candidates.map((c) => [c.id, c]));
  const energyMap = apportionEnergy(event, candidates, opts.preferredPrimaryCellId);

  // Working energy deposits (mutable during crack propagation).
  const eOf = new Map<number, number>();
  const distOf = new Map<number, number>();
  for (const c of candidates) {
    const dep = energyMap.get(c.id);
    eOf.set(c.id, dep?.energy ?? 0);
    distOf.set(
      c.id,
      dep?.dist ??
        Math.hypot(
          c.centroid.x - event.origin.x,
          c.centroid.y - event.origin.y,
          c.centroid.z - event.origin.z,
        ),
    );
  }

  const failed = new Set<number>();

  const thresholdFor = (c: ResolveCandidate): number => {
    const loadDir = loadDirectionForCell(event, c.centroid);
    const intact = thresholdEnergy(physics, event.modeMix, c.volume, loadDir, K);
    const priorDamage = opts.existingDamage?.get(c.id) ?? c.damage;
    return intact * damageStrengthMultiplier(priorDamage);
  };

  const evalFail = (id: number): boolean => {
    const c = byId.get(id);
    if (!c) return false;
    const e = eOf.get(id) ?? 0;
    if (e <= 0) return false;
    const th = thresholdFor(c);
    return e > th;
  };

  // Primary failures from footprint energy.
  for (const c of candidates) {
    if (evalFail(c.id)) failed.add(c.id);
  }

  // Shaped events confine crack propagation to the kernel too. Deposition alone
  // cannot hold a weapon's geometry: the 55% primary peak leaves a large surplus,
  // and one isotropic propagation step across 0.3–1.2 m cells already crosses a
  // 0.6 m wall — measured, the spike's "channel" came out WIDER than the cannon's
  // crater and the rubble "bruise" punched through the back face. Physically the
  // crack front follows the stress field, which is concentrated along the
  // loading axis, so propagated energy is attenuated by the same kernel weight
  // as deposition. Shape-absent events keep weight 1 → bit-identical legacy.
  const propagationShape =
    event.footprint.kind === 'point' &&
    event.footprint.shape &&
    event.direction.kind === 'focused'
      ? event.footprint.shape
      : null;
  const propagationAxis =
    propagationShape && event.direction.kind === 'focused'
      ? norm3(event.direction.vector)
      : null;
  const propagationWeightOf = (id: number): number => {
    if (!propagationShape || !propagationAxis) return 1;
    const c = byId.get(id);
    if (!c) return 0;
    const w = directionalKernelWeight(
      {
        x: c.centroid.x - event.origin.x,
        y: c.centroid.y - event.origin.y,
        z: c.centroid.z - event.origin.z,
      },
      propagationAxis,
      propagationShape,
    );
    // sqrt: a crack front carries energy along itself more efficiently than
    // direct deposition falls off, so propagation is attenuated more gently
    // than the deposit — but sqrt(0) is still 0, which is the part that holds
    // each weapon's shape (nothing crosses the kernel's hard boundary: a spike
    // cannot widen past its channel, a rubble bruise cannot punch the back face).
    return Math.sqrt(w);
  };

  // Crack propagation: transfer surplus energy to static neighbors of failed cells.
  for (let iter = 0; iter < CRACK_PROPAGATION_MAX_ITERS; iter += 1) {
    let added = 0;
    const wave = [...failed];
    for (const fid of wave) {
      const fc = byId.get(fid);
      if (!fc) continue;
      const e = eOf.get(fid) ?? 0;
      const th = thresholdFor(fc);
      const surplus = Math.max(0, e - th);
      if (surplus <= 0) continue;
      const nbs = fc.neighbors.filter((n) => byId.has(n) && !failed.has(n));
      if (nbs.length === 0) continue;
      const share = (CRACK_PROPAGATION_ALPHA * surplus) / nbs.length;
      // Brittle materials propagate hard (glass ~0.95); tough materials stay local.
      const b = physics.brittleness;
      const prop = share * Math.pow(b, 1.35);
      if (prop <= 1e-9) continue;
      for (const n of nbs) {
        const shaped = prop * propagationWeightOf(n);
        if (shaped <= 1e-9) continue;
        eOf.set(n, (eOf.get(n) ?? 0) + shaped);
        if (evalFail(n)) {
          failed.add(n);
          added += 1;
        }
      }
    }
    if (added === 0) break;
  }

  // Catastrophic brittle runaway: once a majority of a highly brittle body has
  // failed, residual cohesion cannot arrest the crack front (glass panes).
  // Rule is brittleness-gated — not a per-material fullRelease flag.
  if (
    physics.brittleness >= 0.9 &&
    candidates.length > 0 &&
    failed.size >= 0.45 * candidates.length
  ) {
    for (const c of candidates) {
      if (failed.has(c.id)) continue;
      failed.add(c.id);
      // Seed a token energy so surplus accounting stays non-zero for impulses.
      const th = thresholdFor(c);
      eOf.set(c.id, Math.max(eOf.get(c.id) ?? 0, th * 1.05));
    }
  }

  // Build deposits + rim.
  const deposits: CellEnergyDeposit[] = [];
  const rimUpdates: RimDamageUpdate[] = [];
  const preCrackCellIds: number[] = [];
  let surplusEnergy = 0;

  for (const c of candidates) {
    const e = eOf.get(c.id) ?? 0;
    const dist = distOf.get(c.id) ?? 0;
    const loadDir = loadDirectionForCell(event, c.centroid);
    const se = effectiveStrength(physics, event.modeMix, loadDir);
    const th = thresholdFor(c);
    const sigma = e / Math.max(c.volume, 1e-9);
    const isFailed = failed.has(c.id);
    deposits.push({
      cellId: c.id,
      energy: e,
      dist,
      volume: c.volume,
      sigma,
      thresholdE: th,
      effectiveStrength: se,
      failed: isFailed,
    });
    if (isFailed) {
      surplusEnergy += Math.max(0, e - th);
    } else if (e > 0 && th > 0) {
      const ratio = e / th;
      // Sub-threshold rim: (0.5, 1.0] × threshold.
      // Near-threshold cells with damageAccumRate ≥ 0.5 pre-crack in one blast.
      if (ratio > 0.5 && ratio <= 1.0) {
        const t = (ratio - 0.5) / 0.5; // 0..1 across the rim band
        const delta = Math.min(1, physics.damageAccumRate * (0.5 + 1.5 * t));
        const prior = opts.existingDamage?.get(c.id) ?? c.damage;
        const next = prior + delta;
        rimUpdates.push({ cellId: c.id, delta, sigmaRatio: ratio });
        if (prior < 1 && next >= 1) {
          preCrackCellIds.push(c.id);
        }
      }
    }
  }

  // Crack-front rim: static neighbors of failed cells always accumulate damage
  // (replaces the old radius annulus when energy is binary fail/survive).
  if (failed.size > 0) {
    const rimSeen = new Set(rimUpdates.map((u) => u.cellId));
    for (const fid of failed) {
      const fc = byId.get(fid);
      if (!fc) continue;
      for (const n of fc.neighbors) {
        if (failed.has(n) || rimSeen.has(n)) continue;
        const nc = byId.get(n);
        if (!nc) continue;
        const delta = Math.min(1, physics.damageAccumRate * 0.85);
        if (delta <= 0) continue;
        const prior = opts.existingDamage?.get(n) ?? nc.damage;
        const next = prior + delta;
        rimUpdates.push({ cellId: n, delta, sigmaRatio: 0.75 });
        rimSeen.add(n);
        if (prior < 1 && next >= 1) {
          preCrackCellIds.push(n);
        }
      }
    }
  }

  const carvedCellIds = [...failed];
  // Dense-near-impact order for sub-fracture.
  carvedCellIds.sort((a, b) => (distOf.get(a) ?? 0) - (distOf.get(b) ?? 0));

  let carvedVolume = 0;
  let totalCandidateVolume = 0;
  for (const c of candidates) {
    totalCandidateVolume += c.volume;
    if (failed.has(c.id)) carvedVolume += c.volume;
  }
  const carvedVolumeFrac =
    totalCandidateVolume > 1e-12 ? carvedVolume / totalCandidateVolume : 0;

  const newSurfaceArea =
    physics.brittleness * surplusEnergy / Math.max(physics.fractureEnergy, 1e-6);

  const carvedForSub = carvedCellIds.map((id) => {
    const c = byId.get(id)!;
    return { cellId: id, volume: c.volume, dist: distOf.get(id) ?? 0 };
  });
  const { plans: subPlans, spent: spentSurfaceArea, totalPieces: subPieces } =
    planSubFractureFromArea(carvedForSub, newSurfaceArea, Math.max(0, subBudget));

  // Impulse: kinetic share = (1 − brittleness) × min(E_k, cap), apportion by falloff
  // (not mass — heavy stone must get lower Δv = √(2 e_k / m) for the same e_k).
  // Omni (bomb) uses BLAST_KINETIC_MUL so carve-calibrated energy still throws
  // rubble (feel parity); focused/cannon stays pure event.energy.
  // Cap is tighter for very strong materials so base-blast boulders don't hop.
  const strengthPeak = Math.max(
    physics.strength.compressive,
    physics.strength.tensile,
    physics.strength.shear,
  );
  const strengthDamp = strengthPeak > 50 ? 0.35 : 1;
  const energyForKinetic =
    event.direction.kind === 'omni'
      ? Math.min(event.energy * BLAST_KINETIC_MUL, IMPULSE_ENERGY_CAP_J)
      : Math.min(event.energy, IMPULSE_ENERGY_CAP_J);
  const kineticPool =
    (1 - physics.brittleness) * energyForKinetic * strengthDamp;
  const impulses: ImpulsePlan[] = [];
  let weightSum = 0;
  const impulseWeights: Array<{ id: number; w: number; mass: number }> = [];
  for (const id of carvedCellIds) {
    const c = byId.get(id)!;
    const dist = distOf.get(id) ?? 0;
    // Reuse footprint weight if in range; else soft 1/r² from origin.
    let w = falloffWeight(dist, event.footprint);
    if (w <= 0) {
      w = 1 / (1 + dist * dist);
    }
    impulseWeights.push({ id, w, mass: Math.max(c.mass, 0.05) });
    weightSum += w;
  }

  let clampedChunks = 0;
  let flooredChunks = 0;
  let maxChunkSpeed = 0;
  const isOmni = event.direction.kind === 'omni';
  // Blast-carved chunks use a dedicated cap; focused/cannon keeps the global max.
  const launchCap = isOmni ? BLAST_MAX_LAUNCH : maxLaunchSpeed;

  for (const { id, w, mass } of impulseWeights) {
    const eK = weightSum > 0 ? (kineticPool * w) / weightSum : 0;
    const massSafe = Math.max(mass, 0.05);
    let speed = Math.sqrt(Math.max(0, (2 * Math.max(eK, 0)) / massSafe));
    if (!Number.isFinite(speed)) speed = 0;
    let clamped = false;
    // Floor first (wedged/low-share chunks), then hard cap.
    // Omni floor scales with charge power (launchScale); focused path is fixed.
    const minLaunch = isOmni ? BLAST_MIN_LAUNCH * launchScale : KINETIC_MIN_LAUNCH;
    if (speed < minLaunch) {
      speed = minLaunch;
      flooredChunks += 1;
    }
    if (speed > launchCap) {
      speed = launchCap;
      clamped = true;
      clampedChunks += 1;
    }
    maxChunkSpeed = Math.max(maxChunkSpeed, speed);

    const c = byId.get(id)!;
    let dir: Vec3;
    if (event.direction.kind === 'focused') {
      // Focused: carry some travel direction, but preferentially eject through
      // the free face from which the projectile arrived so rubble clears the crater.
      const base = norm3(event.direction.vector);
      const faceSign = Math.abs(event.direction.vector.z) > 0.3
        ? -Math.sign(event.direction.vector.z || 1)
        : c.centroid.z >= 0 ? 1 : -1;
      const outOfFace = { x: 0, y: 0, z: faceSign };
      const jx = (rng() * 2 - 1) * 0.12;
      const jy = (rng() * 2 - 1) * 0.12;
      const jz = (rng() * 2 - 1) * 0.12;
      dir = norm3({
        x: base.x * KINETIC_THROUGH + outOfFace.x * KINETIC_OUT_OF_FACE + jx,
        y: base.y * KINETIC_THROUGH + upwardBias * 0.25 + jy,
        z: base.z * KINETIC_THROUGH + outOfFace.z * KINETIC_OUT_OF_FACE + jz,
      });
    } else {
      // Omni: spall exits the wall faces — blend radial from charge with
      // out-of-face normal (±Z away from wall midplane at z≈0). Pure radial
      // jammed fragments inside the 0.6 m thickness (qa11 shattered-in-place).
      const radial = {
        x: c.centroid.x - event.origin.x,
        y: c.centroid.y - event.origin.y,
        z: c.centroid.z - event.origin.z,
      };
      // Nearer face: sign of z relative to wall midplane (wall centered at z=0).
      const faceSign = c.centroid.z >= 0 ? 1 : -1;
      const outOfFace = { x: 0, y: 0, z: faceSign };
      if (len3(radial) < 1e-6) {
        dir = norm3({
          x: outOfFace.x,
          y: upwardBias,
          z: outOfFace.z,
        });
      } else {
        const n = norm3(radial);
        dir = norm3({
          x: n.x * KINETIC_THROUGH + outOfFace.x * KINETIC_OUT_OF_FACE,
          y: n.y * KINETIC_THROUGH + outOfFace.y * KINETIC_OUT_OF_FACE + upwardBias,
          z: n.z * KINETIC_THROUGH + outOfFace.z * KINETIC_OUT_OF_FACE,
        });
      }
    }

    impulses.push({
      cellId: id,
      vx: dir.x * speed,
      vy: dir.y * speed,
      vz: dir.z * speed,
      speed,
      clamped,
    });
  }

  return {
    deposits,
    carvedCellIds,
    carvedVolume,
    carvedVolumeFrac,
    surplusEnergy,
    newSurfaceArea,
    spentSurfaceArea,
    subPlans,
    impulses,
    rimUpdates,
    preCrackCellIds,
    stats: {
      energy: event.energy,
      carvedCells: carvedCellIds.length,
      carvedVolume,
      carvedVolumeFrac,
      surplusEnergy,
      newSurfaceArea,
      spentSurfaceArea,
      subPieces,
      clampedChunks,
      flooredChunks,
      maxChunkSpeed,
    },
  };
}

/** Material physics tables (MPa-like strengths; internally consistent). */
export const MATERIAL_PHYSICS: Record<
  'concrete' | 'brick' | 'wood' | 'stone' | 'glass' | 'plaster',
  MaterialPhysics
> = {
  concrete: {
    strength: { compressive: 30, tensile: 3, shear: 6 },
    fractureEnergy: 120,
    brittleness: 0.55,
    damageAccumRate: 0.5,
  },
  brick: {
    strength: { compressive: 15, tensile: 1.5, shear: 3 },
    fractureEnergy: 60,
    brittleness: 0.5,
    damageAccumRate: 0.7,
  },
  wood: {
    strength: { compressive: 40, tensile: 80, shear: 8 },
    anisotropy: { axis: [1, 0, 0], crossGrainFactor: 0.15 },
    fractureEnergy: 400,
    brittleness: 0.2,
    damageAccumRate: 0.3,
  },
  stone: {
    strength: { compressive: 100, tensile: 8, shear: 15 },
    fractureEnergy: 40,
    brittleness: 0.7,
    damageAccumRate: 0.2,
  },
  glass: {
    strength: { compressive: 50, tensile: 0.5, shear: 5 },
    fractureEnergy: 8,
    brittleness: 0.95,
    damageAccumRate: 1.0,
  },
  /**
   * Gypsum plaster: weaker and lighter than concrete, crumbly (high brittleness
   * + comminution). Compressive ~8 MPa vs concrete's 30; density 1200 vs 2300.
   */
  plaster: {
    strength: { compressive: 8, tensile: 0.8, shear: 1.6 },
    fractureEnergy: 40,
    brittleness: 0.72,
    damageAccumRate: 0.75,
  },
};
